import { useState } from 'react';
import { Modal, Stack, TextInput, Button, Group, Alert, Text, Code } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api } from '../../api/client';

export default function DangerousDeleteModal({
  equipmentId,
  equipmentName,
  onClose,
  onDeleted,
}: {
  equipmentId: number;
  equipmentName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doDelete() {
    setErr(null);
    setBusy(true);
    try {
      await api.delete(`/equipment/${equipmentId}`, { confirm });
      notifications.show({ message: `${equipmentName} deleted.`, color: 'gray' });
      onDeleted();
    } catch (e: any) {
      setErr(e.payload?.hint ? `${e.message} — ${e.payload.hint}` : e.message ?? 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={onClose} title={`Delete "${equipmentName}"`}>
      <Stack>
        <Alert color="red" variant="light">
          This will permanently delete the equipment, all its accessories, and status log.
          Attached consumables are <strong>not</strong> deleted.
        </Alert>
        <Text size="sm">
          To confirm, enter the <strong>G-code</strong> command that moves the CNC spindle <strong>666 mm on the Z axis</strong>:
        </Text>
        <Text size="xs" c="dimmed">Hint: a linear move at programmed feed rate, targeting Z=666.</Text>
        <TextInput
          placeholder="e.g. G1 Z666"
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          autoFocus
          classNames={{ input: 'cnc-mono' }}
        />
        <Text size="xs" c="dimmed">
          The exact phrase expected by the server is <Code>move 666 mm on Z axis</Code> (the typical human-readable spelling of that G-code move).
        </Text>
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color="red" onClick={doDelete} loading={busy}>I understand — delete</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
